"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"

type Props = {
  cities: string[]
  selected: string | null
  onChange: (city: string | null) => void
}

export function CityPicker({ cities, selected, onChange }: Props) {
  const t = useTranslations("marketplace.filters")
  const [detecting, setDetecting] = useState(false)

  useEffect(() => {
    // Si la URL ya trae ?city= (enlace compartido / recarga), respetarla: la
    // autodetección no debe pisar un filtro explícito.
    if (selected) return
    if (!navigator.geolocation) return
    setDetecting(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`,
            {
              headers: { "User-Agent": "Redy/1.0 contact@useredy.com" },
              signal: AbortSignal.timeout(5000),
            },
          )
          const data = await res.json()
          const detected: string | null =
            data?.address?.city ??
            data?.address?.town ??
            data?.address?.village ??
            null

          if (detected) {
            const match = cities.find(
              (c) => c.toLowerCase() === detected.toLowerCase(),
            )
            if (match) onChange(match)
          }
        } catch {
          // Silently fail — show all merchants, no pre-selection
        } finally {
          setDetecting(false)
        }
      },
      () => {
        // Geolocation denied — show all merchants
        setDetecting(false)
      },
      { timeout: 5000 },
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Fire once on mount only

  if (cities.length === 0) return null

  return (
    <div className="flex items-center gap-2">
      {detecting && (
        <span className="text-[12px] text-[#7A7A7A]">{t("detectingLocation")}</span>
      )}
      <select
        value={selected ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="bg-white border border-[#E8E4DF] rounded-xl px-3 py-2 text-[13px] text-[#1A1A1A] focus:outline-none focus:border-[#D7263D] appearance-none pr-8 cursor-pointer"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A7A7A' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 10px center",
        }}
        aria-label={t("cityAria")}
      >
        <option value="">{t("allCities")}</option>
        {cities.map((city) => (
          <option key={city} value={city}>
            {city}
          </option>
        ))}
      </select>
    </div>
  )
}
